import React, { useEffect, useState } from 'react'
import { getGpaPercentAverages, getLessonFutureLevelAverage, getOverviewTexts, saveOverviewTexts } from '../../../lib/studentgparigorsummary'
import { ReportCard, ToastNotification, OverviewTextarea, UpdateButton } from './CourseReportComponents'
import { getHeaderClass, sortLevels } from './CourseReportUtils'
import style from './CourseReportStyles.module.css'

export const GPASummary = ({ studentId, isStudent = false }) => {
	const [current, setCurrent] = useState({})
	const [future, setFuture] = useState({})
	const [overview, setOverview] = useState({})
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [levels, setLevels] = useState([])

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const loadData = async () => {
		const currentRes = await getGpaPercentAverages(studentId)
		const futureRes = await getLessonFutureLevelAverage(studentId)
		const overviewRes = await getOverviewTexts(studentId)

		const currentMap = {}
		const futureMap = {}
		const levelSet = new Set()

		currentRes.forEach((item) => {
			const lvl = item.level === 'Overall' ? 'Ovr' : item.level
			currentMap[lvl] = item.average ?? 0
			levelSet.add(lvl)
		})

		futureRes.forEach((item) => {
			const lvl = item.level === 'Overall' ? 'Ovr' : item.level
			futureMap[lvl] = item.average ?? 0
			levelSet.add(lvl)
		})

		const overviewMap = {}
		overviewRes.forEach((item) => {
			overviewMap[item.type] = item.desc
		})

		setCurrent(currentMap)
		setFuture(futureMap)
		setOverview(overviewMap)
		setLevels(['Ovr', ...sortLevels([...levelSet].filter((l) => l !== 'Ovr'))])
	}

	useEffect(() => {
		loadData()
	}, [studentId])

	const handleOverviewChange = (value) => {
		setOverview((prev) => ({ ...prev, caverage: value }))
	}

	const handleUpdate = async () => {
		await saveOverviewTexts(studentId, { caverage: overview.caverage || '' })
		showToastMessage('Update successful!')
		loadData()
	}

	const getValue = (map, level) => {
		if (level === 'Ovr') {
			const values = levels
				.filter((lvl) => lvl !== 'Ovr')
				.map((lvl) => parseFloat(map[lvl] || 0))
				.filter((val) => val > 0)

			if (values.length === 0) return '0.0'

			const sum = values.reduce((acc, val) => acc + val, 0)
			return (sum / values.length).toFixed(1)
		}

		return map[level] !== undefined ? parseFloat(map[level]).toFixed(1) : '0.0'
	}

	return (
		<ReportCard title="GPA">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />

			<div>
				<table className="table table-bordered text-center">
					<thead>
						<tr>
							<th style={{ width: '150px' }}></th>
							{levels.map((lvl) => (
								<th key={lvl} className={style[getHeaderClass(lvl)]}>
									{lvl}
								</th>
							))}
							<th style={{ width: '400px' }}>Overview</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>Current</td>
							{levels.map((lvl) => {
								const val = parseFloat(getValue(current, lvl))
								return (
									<td key={lvl} className={val.toFixed(1) > 0 ? style[getHeaderClass(lvl)] : ''}>
										{val.toFixed(1)}
									</td>
								)
							})}
							<td rowSpan={3}>
								<OverviewTextarea value={overview['caverage'] || ''} onChange={handleOverviewChange} rows={6} isStudent={isStudent} />
							</td>
						</tr>
						<tr>
							<td>Future</td>
							{levels.map((lvl) => {
								const val = parseFloat(getValue(future, lvl))
								return (
									<td key={lvl} className={val.toFixed(1) > 0 ? style[getHeaderClass(lvl)] : ''}>
										{val.toFixed(1)}
									</td>
								)
							})}
						</tr>
						<tr>
							<td>Total</td>
							{levels.map((lvl) => {
								if (lvl === 'Ovr') {
									// Collect all current and future values for all levels except Ovr
									const allValues = levels
										.filter((l) => l !== 'Ovr')
										.flatMap((l) => [parseFloat(current[l] || 0), parseFloat(future[l] || 0)])
										.filter((val) => val > 0)
									const overallAvg = allValues.length > 0 ? (allValues.reduce((a, b) => a + b, 0) / allValues.length).toFixed(1) : '0.0'
									return (
										<td key={lvl} className={overallAvg > 0 ? style[getHeaderClass(lvl)] : ''}>
											{overallAvg}
										</td>
									)
								} else {
									const curr = parseFloat(current[lvl] || 0)
									const fut = parseFloat(future[lvl] || 0)
									const values = []
									if (curr > 0) values.push(curr)
									if (fut > 0) values.push(fut)
									const avg = values.length > 0 ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1) : '0.0'
									return (
										<td key={lvl} className={(avg > 0 ? style[getHeaderClass(lvl)] : '') + ' fw-bold'}>
											{avg}
										</td>
									)
								}
							})}
						</tr>
					</tbody>
				</table>

				<UpdateButton onClick={handleUpdate} isStudent={isStudent} />
			</div>
		</ReportCard>
	)
}
